"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { getTeacherData } from "./api" // Fixed import path
import { postAws } from "../postAws" // Fixed import path
import { generatePresignedUrl } from "../postFileAws"
import {
  User,
  Book,
  Phone,
  MapPin,
  Plus,
  Loader2,
  Trash2,
  Mail,
  Edit,
  X,
  Check,
  UserPlus,
  Copy,
  Clock,
  Calendar,
} from "lucide-react"

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState("")
  const navigate = useNavigate()
  const [enrolledClasses, setEnrolledClasses] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [flashMessage, setFlashMessage] = useState(null)
  const [teacherFetchData, setTeacherData] = useState()
  const [showCreateClassModal, setShowCreateClassModal] = useState(false)
  // Update the newClassData state to include schedule
  const [newClassData, setNewClassData] = useState({
    classValue: "",
    subject: "",
    batchSize: "",
    section: "",
    classDescription: "",
    teacherId: localStorage.getItem("teacherId"),
    organisationId: localStorage.getItem("organisationId") || "",
    schedule: [], // New field for class schedule
  })
  const [isCreatingClass, setIsCreatingClass] = useState(false)
  const [isOtherClass, setIsOtherClass] = useState(false)
  const [isOtherSubject, setIsOtherSubject] = useState(false)
  const [isDeletingClass, setIsDeletingClass] = useState(false)
  const [classToDelete, setClassToDelete] = useState(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [profileImageUrl, setProfileImageUrl] = useState("")

  // Organization request states
  const [showOrgRequestForm, setShowOrgRequestForm] = useState(false)
  const [orgRequestData, setOrgRequestData] = useState({
    organisationId: "",
  })
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)
  const [requestMessage, setRequestMessage] = useState(null)

  const classOptions = ["9", "10", "11", "12", "Other"]
  const subjectOptions = [
    "Mathematics",
    "Science",
    "Social Science",
    "English",
    "Hindi",
    "Physics",
    "Chemistry",
    "Biology",
    "Computer Science",
    "Other",
  ]

  const daysOfWeek = [
    { id: "monday", name: "Monday", short: "Mon" },
    { id: "tuesday", name: "Tuesday", short: "Tue" },
    { id: "wednesday", name: "Wednesday", short: "Wed" },
    { id: "thursday", name: "Thursday", short: "Thu" },
    { id: "friday", name: "Friday", short: "Fri" },
    { id: "saturday", name: "Saturday", short: "Sat" },
    { id: "sunday", name: "Sunday", short: "Sun" },
  ]

  // Schedule management functions
  const addScheduleSlot = () => {
    setNewClassData({
      ...newClassData,
      schedule: [
        ...newClassData.schedule,
        {
          id: Date.now(),
          day: "",
          startTime: "",
          endTime: "",
        },
      ],
    })
  }

  const removeScheduleSlot = (id) => {
    setNewClassData({
      ...newClassData,
      schedule: newClassData.schedule.filter((slot) => slot.id !== id),
    })
  }

  const updateScheduleSlot = (id, field, value) => {
    setNewClassData({
      ...newClassData,
      schedule: newClassData.schedule.map((slot) => (slot.id === id ? { ...slot, [field]: value } : slot)),
    })
  }

  const formatScheduleDisplay = (schedule) => {
    if (!schedule || schedule.length === 0) return null

    return schedule.map((slot, index) => {
      const day = daysOfWeek.find((d) => d.id === slot.day)
      return (
        <div key={index} className="flex items-center justify-between bg-indigo-50 px-3 py-2 rounded-lg">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-indigo-600" />
            <span className="font-medium text-indigo-800">{day?.short}</span>
          </div>
          <div className="flex items-center space-x-1 text-sm text-indigo-600">
            <Clock className="w-3 h-3" />
            <span>
              {slot.startTime} - {slot.endTime}
            </span>
          </div>
        </div>
      )
    })
  }

  // Update the fetchteacherInfo function to set organizationId in newClassData when data is fetched
  const fetchteacherInfo = useCallback(async () => {
    try {
      setIsLoading(true)
      const teacherId = localStorage.getItem("teacherId")
      const response = await getTeacherData(teacherId)
      if (response.statusCode === 200 || response.statusCode === 201) {
        const data = response.body.data
        setTeacherData(data)
        setEnrolledClasses(data.createdClasses)

        // If organization is approved, save to local storage
        if (data.enrollmentRequestToOrg === "approved" && data.organisationId && data.organisationName) {
          localStorage.setItem("organisationId", data.organisationId)
          localStorage.setItem("organisationName", data.organisationName)

          // Update newClassData with the organization ID
          setNewClassData((prev) => ({
            ...prev,
            organisationId: data.organisationId,
          }))
        }

        if (data.profilePhotoS3Uri) {
          const presignedUrl = await generatePresignedUrl(data.profilePhotoS3Uri)
          setProfileImageUrl(presignedUrl)
        }
      } else {
        throw new Error(response.body.message || "Failed to fetch teacher information")
      }
    } catch (error) {
      console.error("Error fetching teacher info:", error)
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to fetch teacher information. Please try again.",
      })
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchteacherInfo()
  }, [fetchteacherInfo])

  useEffect(() => {
    if (flashMessage) {
      const timer = setTimeout(() => {
        setFlashMessage(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [flashMessage])

  const confirmOrgRequest = () => {
    setOrgRequestData({
      ...orgRequestData,
      confirmed: true,
    })
    // Re-submit the form with confirmation
    handleOrgRequestSubmit(new Event("submit"))
  }

  const handleOrgRequestSubmit = async (e) => {
    e.preventDefault()
    setIsSubmittingRequest(true)

    try {
      const teacherId = localStorage.getItem("teacherId")
      const hasCreatedClasses = enrolledClasses.length > 0

      // If user has created classes, show confirmation dialog first
      if (hasCreatedClasses && !orgRequestData.confirmed) {
        setRequestMessage({
          type: "warning",
          text: "Warning: If you attach yourself to an organization, your current data will be removed. We recommend creating a new account with a different ID to join an organization instead.",
          needsConfirmation: true,
        })
        setIsSubmittingRequest(false)
        return
      }

      const requestData = {
        request: "teacher-admin-enroll",
        body: {
          userId: teacherId,
          name: teacherFetchData.teacherName,
          email: teacherFetchData.teacherEmail,
          phone: teacherFetchData.phoneNumber,
          organisationId: orgRequestData.organisationId,
        },
      }

      const response = await postAws("https://api-dev.aurait.ai/orgnaisational-admin", "POST", requestData)

      if (response.statusCode === 200) {
        setRequestMessage({
          type: "success",
          text: "Enrollment request sent successfully! You will be notified when approved.",
        })
        setFlashMessage({
          type: "success",
          message: "Enrollment request sent successfully!",
        })

        // Refresh teacher data to get updated enrollment status
        fetchteacherInfo()
      } else if (response.body?.error?.includes("organisation") || response.body?.message?.includes("organisation")) {
        // Handle case when organization doesn't exist
        setRequestMessage({
          type: "error",
          text: "No such organisation exists. Please check the ID and try again.",
          allowRetry: true,
        })
      } else {
        throw new Error(response.body?.error || "Failed to send enrollment request")
      }
    } catch (error) {
      console.error("Error sending enrollment request:", error)
      setRequestMessage({
        type: "error",
        text: error.message || "Failed to send enrollment request. Please try again.",
        allowRetry: true,
      })
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to send enrollment request. Please try again.",
      })
    } finally {
      setIsSubmittingRequest(false)
    }
  }

  const handleContact = () => {
    navigate("/Communication")
  }

  // Update the handleCreateClass function to include schedule
  const handleCreateClass = useCallback(
    async (e) => {
      e.preventDefault()
      setIsCreatingClass(true)

      // Check if user is part of an organization
      const orgId =
        localStorage.getItem("organisationId") ||
        (teacherFetchData?.enrollmentRequestToOrg === "approved" ? teacherFetchData?.organisationId : "")

      const classData = {
        body: {
          ...newClassData,
          classValue: isOtherClass ? newClassData.classValue : newClassData.classValue,
          subject: isOtherSubject ? newClassData.subject : newClassData.subject,
          organisationId: orgId || "", // Include organization ID if available
          schedule: newClassData.schedule, // Include schedule
        },
        request: "create-Class",
      }

      try {
        const response = await postAws("https://api-dev.aurait.ai/classroom", "POST", classData)
        console.log(response)
        setShowCreateClassModal(false)
        const updatedTeacherData = await getTeacherData(localStorage.getItem("teacherId"))
        setEnrolledClasses(updatedTeacherData.body.data.createdClasses || [])
        setFlashMessage({
          type: "success",
          message: "Class created successfully!",
        })
        window.location.reload()
      } catch (err) {
        console.error("Error creating class:", err)
        setFlashMessage({
          type: "error",
          message: "Failed to create class. Please try again.",
        })
      } finally {
        setIsCreatingClass(false)
      }
    },
    [newClassData, isOtherClass, isOtherSubject, teacherFetchData],
  )

  const handleClassChange = (e) => {
    const value = e.target.value
    setIsOtherClass(value === "Other")
    setNewClassData({ ...newClassData, classValue: value === "Other" ? "" : value })
  }

  const handleSubjectChange = (e) => {
    const value = e.target.value
    setIsOtherSubject(value === "Other")
    setNewClassData({ ...newClassData, subject: value === "Other" ? "" : value })
  }

  const handleDeleteClass = (classInfo) => {
    setClassToDelete(classInfo)
    setShowDeleteConfirmation(true)
  }

  const handleprofileedit = () => {
    navigate("/teacher/profile-edit")
  }

  const confirmDeleteClass = async () => {
    if (classToDelete && classToDelete.confirmText === "permanently delete") {
      setIsDeletingClass(true)
      try {
        const response = await postAws("https://api-dev.aurait.ai/classroom", "POST", {
          request: "delete-class",
          body: {
            classId: classToDelete.classRoomId,
            teacherId: localStorage.getItem("teacherId"),
          },
        })

        if (response.statusCode === 200) {
          setFlashMessage({
            type: "success",
            message: "Class deleted successfully!",
          })
          setEnrolledClasses(enrolledClasses.filter((c) => c.classRoomId !== classToDelete.classRoomId))
          setTimeout(() => {
            window.location.reload()
          }, 1500)
        } else {
          throw new Error(response.body.message || "Failed to delete class")
        }
      } catch (error) {
        console.error("Error deleting class:", error)
        setFlashMessage({
          type: "error",
          message: "Failed to delete class. Please try again.",
        })
      } finally {
        setIsDeletingClass(false)
        setShowDeleteConfirmation(false)
        setClassToDelete(null)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center justify-center space-y-4">
          <Loader2 size={40} className="text-indigo-600 animate-spin" />
          <p className="text-indigo-800 font-medium text-lg">Loading your profile..</p>
        </div>
      </div>
    )
  }

  // Update the check for whether a user has an organization
  // Check if teacher has an organization attached
  const hasOrganization =
    (teacherFetchData?.organisationId &&
      teacherFetchData?.organisationName &&
      teacherFetchData?.enrollmentRequestToOrg === "approved") ||
    localStorage.getItem("organisationId")
  const hasPendingRequest = teacherFetchData?.enrollmentRequestToOrg === "sent"
  const hasRejectedRequest = teacherFetchData?.enrollmentRequestToOrg === "rejected"

  const getSubjectColor = (subject) => {
    const colors = {
      Mathematics: "bg-purple-100 text-purple-800",
      Science: "bg-green-100 text-green-800",
      "Social Science": "bg-yellow-100 text-yellow-800",
      English: "bg-blue-100 text-blue-800",
      Hindi: "bg-orange-100 text-orange-800",
      Physics: "bg-indigo-100 text-indigo-800",
      Chemistry: "bg-pink-100 text-pink-800",
      Biology: "bg-emerald-100 text-emerald-800",
      "Computer Science": "bg-cyan-100 text-cyan-800",
    }
    return colors[subject] || "bg-gray-100 text-gray-800"
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      {flashMessage && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 transform transition-all duration-500 ease-in-out animate-fade-in ${
            flashMessage.type === "error" ? "bg-red-500" : "bg-green-500"
          } text-white max-w-xs sm:max-w-sm`}
        >
          {flashMessage.type === "error" ? (
            <X className="h-5 w-5 flex-shrink-0" />
          ) : (
            <Check className="h-5 w-5 flex-shrink-0" />
          )}
          <p>{flashMessage.message}</p>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Organization Status Banner removed */}

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8 transition-all duration-300 hover:shadow-2xl">
          <div className="md:flex">
            {/* Profile Image Section */}
            <div className="md:w-1/3 lg:w-1/4 relative">
              <div className="h-56 md:h-64 w-full relative overflow-hidden">
                {profileImageUrl ? (
                  <img
                    className="h-full w-full object-contain transition-transform duration-700 hover:scale-105"
                    src={profileImageUrl || "/placeholder.svg"}
                    alt={teacherFetchData?.teacherName}
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-indigo-100 to-blue-200 flex items-center justify-center">
                    <User size={80} className="text-indigo-400" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h1 className="text-white text-xl md:text-2xl font-bold tracking-tight drop-shadow-md">
                    {teacherFetchData?.teacherName || "Teacher Name"}
                  </h1>
                </div>
              </div>
            </div>

            {/* Profile Info Section */}
            <div className="p-4 md:p-6 flex-grow">
              <div className="flex flex-col h-64">
                <div className="flex-grow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="uppercase tracking-wide text-sm text-indigo-600 font-bold bg-indigo-50 px-3 py-1 rounded-full inline-block">
                      Teacher Profile
                    </div>
                    <button
                      onClick={handleprofileedit}
                      className="bg-indigo-50 text-indigo-600 p-2 rounded-full hover:bg-indigo-100 transition-colors duration-300"
                      aria-label="Edit profile"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    {/* Email and Phone row */}
                    <div className="flex flex-col md:flex-row gap-3">
                      <div className="flex items-center text-gray-700 bg-gray-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-gray-100 flex-1">
                        <Mail className="w-4 h-4 mr-2 text-indigo-500" />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Email Address</p>
                          <p className="font-medium text-sm">
                            {teacherFetchData?.teacherEmail || "Email not provided"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center text-gray-700 bg-gray-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-gray-100 flex-1">
                        <Phone className="w-4 h-4 mr-2 text-indigo-500" />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Phone Number</p>
                          <p className="font-medium text-sm">
                            {teacherFetchData?.phoneNumber || "Phone number not provided"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Address row */}
                    <div className="flex items-center text-gray-700 bg-gray-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-gray-100">
                      <MapPin className="w-4 h-4 mr-2 text-indigo-500" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Address</p>
                        <p className="font-medium text-sm">
                          {teacherFetchData?.teacherAddress || "Address not provided"}
                        </p>
                      </div>
                    </div>

                    {/* Organization section */}
                    {hasOrganization ? (
                      <div className="flex items-center text-gray-700 bg-gray-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-gray-100">
                        <UserPlus className="w-4 h-4 mr-2 text-indigo-500" />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Organisation</p>
                          <p className="font-medium text-sm">{teacherFetchData?.organisationName || "Not Available"}</p>
                        </div>
                      </div>
                    ) : hasPendingRequest ? (
                      <div className="flex items-center text-gray-700 bg-blue-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-blue-100">
                        <UserPlus className="w-4 h-4 mr-2 text-blue-500" />
                        <div className="flex-grow">
                          <p className="text-xs text-gray-500 font-medium">Organisation</p>
                          <div className="flex items-center">
                            <p className="font-medium text-sm">
                              {teacherFetchData?.organisationName || "Request Sent"}
                            </p>
                            <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                              Pending
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : hasRejectedRequest ? (
                      <div className="flex items-center text-gray-700 bg-red-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-red-100">
                        <UserPlus className="w-4 h-4 mr-2 text-red-500" />
                        <div className="flex-grow">
                          <p className="text-xs text-gray-500 font-medium">Organisation</p>
                          <div className="flex items-center">
                            <p className="font-medium text-sm">
                              {teacherFetchData?.organisationName || "Request Rejected"}
                            </p>
                            <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                              Rejected
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowOrgRequestForm(!showOrgRequestForm)}
                          className="ml-2 text-xs text-red-600 hover:text-red-700 underline"
                        >
                          Try Again
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowOrgRequestForm(!showOrgRequestForm)}
                        className="flex items-center justify-center text-indigo-600 bg-indigo-50 p-2.5 rounded-lg transition-all duration-300 hover:bg-indigo-100 w-full"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        <span className="font-medium text-sm">Join Organisation</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Organization Request Form */}
        {showOrgRequestForm && (
          <div className="mb-8 bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Join Organisation</h3>

              {requestMessage ? (
                <div
                  className={`p-3 rounded-md ${
                    requestMessage.type === "success"
                      ? "bg-green-50 text-green-800"
                      : requestMessage.type === "warning"
                        ? "bg-yellow-50 text-yellow-800"
                        : "bg-red-50 text-red-800"
                  }`}
                >
                  <p className="text-sm font-medium">{requestMessage.text}</p>

                  {requestMessage.needsConfirmation && (
                    <div className="mt-4 flex space-x-3">
                      <button
                        onClick={() => setRequestMessage(null)}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmOrgRequest}
                        className="px-3 py-1.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium"
                      >
                        Yes, Continue Anyway
                      </button>
                    </div>
                  )}

                  {requestMessage.allowRetry && (
                    <div className="mt-4 flex space-x-3">
                      <button
                        onClick={() => setRequestMessage(null)}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                      >
                        Try Again
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleOrgRequestSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="organisationId" className="block text-sm font-medium text-gray-700">
                      Organisation ID
                    </label>
                    <input
                      type="text"
                      id="organisationId"
                      name="organisationId"
                      value={orgRequestData.organisationId}
                      onChange={(e) => setOrgRequestData({ ...orgRequestData, organisationId: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Enter organisation ID"
                      required
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowOrgRequestForm(false)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmittingRequest}
                      className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 flex items-center justify-center shadow-md hover:shadow-lg transform hover:-translate-y-1"
                    >
                      {isSubmittingRequest ? (
                        <>
                          <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                          Sending Request...
                        </>
                      ) : enrolledClasses.length > 0 ? (
                        "Request to Join Organisation"
                      ) : (
                        "Join Organisation"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        <div className="bg-white rounded-b-2xl shadow-xl overflow-hidden mb-8">
          <div className="p-6 md:p-8 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                <span className="bg-indigo-100 text-indigo-600 p-2 rounded-lg mr-3">
                  <Book className="w-5 h-5" />
                </span>
                Created Classes
              </h2>
            </div>

            {enrolledClasses.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {enrolledClasses.map((classInfo, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
                  >
                    <div className="bg-gradient-to-r from-indigo-500 to-blue-600 h-3"></div>
                    <div className="p-5 relative">
                      <button
                        onClick={() => handleDeleteClass(classInfo)}
                        className="absolute top-3 right-3 p-2 bg-red-50 text-red-500 rounded-full hover:bg-red-100 transition-colors duration-300 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all"
                        aria-label="Delete class"
                      >
                        <Trash2 size={16} />
                      </button>

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-baseline gap-1.5">
                          <h3 className="text-3xl font-bold text-gray-800">{classInfo.classValue}</h3>
                          <span className="text-2xl font-medium text-gray-600">{classInfo.section}</span>
                        </div>
                        <span
                          className={`text-xs font-medium px-2.5 py-1 rounded-full ${getSubjectColor(classInfo.subject)}`}
                        >
                          {classInfo.subject}
                        </span>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between bg-gray-50 p-2.5 rounded-lg">
                          <span className="text-gray-500 text-sm font-medium">Batch Size</span>
                          <span className="text-gray-800 font-semibold">{classInfo.batchSize}</span>
                        </div>

                        <div className="flex items-center justify-between bg-gray-50 p-2.5 rounded-lg">
                          <span className="text-gray-500 text-sm font-medium">Class Code</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono bg-indigo-50 text-indigo-600 px-2 py-1 rounded text-sm font-semibold">
                              {classInfo.classCode}
                            </span>
                            <button
                              onClick={() => navigator.clipboard.writeText(classInfo.classCode)}
                              title="Copy class code"
                              className="text-indigo-600 hover:text-indigo-800 transition-colors"
                            >
                              <Copy size={16} />
                            </button>
                          </div>
                        </div>

                        {/* Schedule Display */}
                        {classInfo.schedule && classInfo.schedule.length > 0 && (
                          <div className="bg-gray-50 p-2.5 rounded-lg">
                            <div className="flex items-center mb-2">
                              <Clock className="w-4 h-4 text-gray-500 mr-2" />
                              <span className="text-gray-500 text-sm font-medium">Class Schedule</span>
                            </div>
                            <div className="space-y-1">{formatScheduleDisplay(classInfo.schedule)}</div>
                          </div>
                        )}
                      </div>

                      <div className="mt-5 pt-4 border-t border-gray-100"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="bg-indigo-50 p-4 rounded-full mb-4">
                  <Book className="w-8 h-8 text-indigo-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No Classes Created Yet</h3>
                <p className="text-gray-500 max-w-md mb-6">
                  Create your first class to start managing your teaching schedule and students.
                </p>
                <button
                  onClick={() => setShowCreateClassModal(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center"
                >
                  <Plus size={18} className="mr-2" />
                  <span>Create Your First Class</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Class Modal */}
      {showCreateClassModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl transform transition-all duration-300 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 py-6 px-8">
              <h2 className="text-2xl font-bold text-white">Create New Class</h2>
              <p className="text-indigo-100 text-sm mt-1">Fill in the details to create a new class</p>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              <form onSubmit={handleCreateClass} className="p-8">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="className" className="block text-sm font-medium text-gray-700 mb-1">
                        Class
                      </label>
                      <select
                        id="classValue"
                        name="classValue"
                        value={isOtherClass ? "Other" : newClassData.classValue}
                        onChange={handleClassChange}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                        required
                      >
                        <option value="">Select a class</option>
                        {classOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {isOtherClass && (
                        <input
                          type="text"
                          placeholder="Enter custom class"
                          value={newClassData.classValue}
                          onChange={(e) => setNewClassData({ ...newClassData, classValue: e.target.value })}
                          className="mt-2 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                          required
                        />
                      )}
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={isOtherSubject ? "Other" : newClassData.subject}
                        onChange={handleSubjectChange}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                        required
                      >
                        <option value="">Select a subject</option>
                        {subjectOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {isOtherSubject && (
                        <input
                          type="text"
                          placeholder="Enter custom subject"
                          value={newClassData.subject}
                          onChange={(e) => setNewClassData({ ...newClassData, subject: e.target.value })}
                          className="mt-2 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                          required
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="batchSize" className="block text-sm font-medium text-gray-700 mb-1">
                        Batch Size
                      </label>
                      <input
                        type="number"
                        id="batchSize"
                        name="batchSize"
                        min="0"
                        value={newClassData.batchSize}
                        onChange={(e) => {
                          const value = Number.parseInt(e.target.value, 10)
                          if (!isNaN(value) && value >= 0) {
                            setNewClassData({ ...newClassData, batchSize: value })
                          } else if (e.target.value === "") {
                            setNewClassData({ ...newClassData, batchSize: "" })
                          }
                        }}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="section" className="block text-sm font-medium text-gray-700 mb-1">
                        Section
                      </label>
                      <input
                        type="text"
                        id="section"
                        name="section"
                        value={newClassData.section}
                        onChange={(e) => {
                          const onlyLetters = e.target.value.replace(/[^a-zA-Z]/g, "")
                          setNewClassData({ ...newClassData, section: onlyLetters })
                        }}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                        required
                      />
                    </div>
                  </div>

                  {/* Schedule Section */}
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <Calendar className="w-5 h-5 text-indigo-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-800">Class Schedule</h3>
                      </div>
                      <button
                        type="button"
                        onClick={addScheduleSlot}
                        className="px-3 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors duration-300 flex items-center text-sm font-medium"
                      >
                        <Plus size={16} className="mr-1" />
                        Add Time Slot
                      </button>
                    </div>

                    {newClassData.schedule.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                        <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">No schedule added yet</p>
                        <p className="text-gray-400 text-xs mt-1">
                          Click "Add Time Slot" to create your class schedule
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {newClassData.schedule.map((slot) => (
                          <div
                            key={slot.id}
                            className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-xl border border-indigo-100"
                          >
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                                <select
                                  value={slot.day}
                                  onChange={(e) => updateScheduleSlot(slot.id, "day", e.target.value)}
                                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                                  required
                                >
                                  <option value="">Select day</option>
                                  {daysOfWeek.map((day) => (
                                    <option key={day.id} value={day.id}>
                                      {day.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                                <input
                                  type="time"
                                  value={slot.startTime}
                                  onChange={(e) => updateScheduleSlot(slot.id, "startTime", e.target.value)}
                                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                                  required
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                                <input
                                  type="time"
                                  value={slot.endTime}
                                  onChange={(e) => updateScheduleSlot(slot.id, "endTime", e.target.value)}
                                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                                  required
                                />
                              </div>

                              <div>
                                <button
                                  type="button"
                                  onClick={() => removeScheduleSlot(slot.id)}
                                  className="w-full px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-300 flex items-center justify-center"
                                >
                                  <Trash2 size={16} className="mr-1" />
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-8 flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateClassModal(false)
                      setNewClassData({
                        classValue: "",
                        subject: "",
                        batchSize: "",
                        section: "",
                        classDescription: "",
                        teacherId: localStorage.getItem("teacherId"),
                        organisationId: localStorage.getItem("organisationId") || "",
                        schedule: [],
                      })
                      setIsOtherClass(false)
                      setIsOtherSubject(false)
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center justify-center shadow-md font-medium"
                    disabled={isCreatingClass}
                  >
                    {isCreatingClass ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        Creating...
                      </>
                    ) : (
                      "Create Class"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl transform transition-all duration-300 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-red-600 to-pink-600 py-6 px-8">
              <h2 className="text-2xl font-bold text-white flex items-center">
                <Trash2 className="w-5 h-5 mr-2" />
                Delete Class
              </h2>
              <p className="text-red-100 text-sm mt-1">This action cannot be undone</p>
            </div>

            <div className="p-8">
              <div className="bg-red-50 p-4 rounded-lg mb-6">
                <p className="text-red-800 font-medium">Are you sure you want to delete this class?</p>
                <p className="text-red-600 text-sm mt-1">All associated data will be permanently removed.</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type "permanently delete" to confirm:
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                  placeholder="Type here"
                  onChange={(e) => setClassToDelete({ ...classToDelete, confirmText: e.target.value })}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirmation(false)
                    setClassToDelete(null)
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteClass}
                  disabled={classToDelete?.confirmText !== "permanently delete" || isDeletingClass}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300 flex items-center justify-center shadow-md font-medium disabled:bg-red-300 disabled:cursor-not-allowed"
                >
                  {isDeletingClass ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      Deleting...
                    </>
                  ) : (
                    "Delete Class"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
